// src/services/connectionsService.ts
import { logger } from '../utils/logger';
// FIXED: Added is_admin to all user profile types

import { supabase } from './supabase';

/**
 * Helper function to send push notification via Supabase Edge Function
 */
async function sendPushNotification(payload: {
  recipientId: string;
  senderId: string;
  type: string;
  matchId?: string;
  [key: string]: any;
}): Promise<void> {
  try {
    const { data, error } = await supabase.functions.invoke('send-notification', {
      body: payload
    });

    if (error) {
      logger.error('Error sending push notification:', error);
    } else {
      logger.log('✅ Push notification sent:', data);
    }
  } catch (error) {
    logger.error('Error invoking notification function:', error);
  }
}

export interface Match {
  id: string;
  user1_id: string;
  user2_id: string;
  status: 'pending' | 'accepted' | 'rejected';
  initiated_by: string;
  created_at: string;
  updated_at: string;
}

export interface SendConnectionResult {
  success: boolean;
  error?: string;
  match?: Match;
}

export interface Connection {
  id: string;
  match_id: string;
  user1_id: string;
  user2_id: string;
  status: 'pending' | 'accepted' | 'rejected';
  initiated_by: string;
  created_at: string;
  updated_at: string;
  other_user: {
    id: string;
    username: string;
    display_name: string;
    avatar_url: string | null;
    avatar?: any;
    bio?: string;
    is_admin?: boolean;
  };
  otherUserId: string;
  otherUser?: {
    id: string;
    username: string;
    display_name: string;
    avatar_url: string | null;
    avatar?: any;
    bio?: string;
    is_admin?: boolean;
  };
}

// Wrapper types for consistent API
export interface ConnectionsResult {
  success: boolean;
  connections: Connection[];
  error?: string;
}

export interface MatchesResult {
  success: boolean;
  connections: Match[];
  error?: string;
}

// Event emitter for connection updates
type ConnectionUpdateListener = () => void;
const connectionUpdateListeners: ConnectionUpdateListener[] = [];

export function onConnectionUpdate(listener: ConnectionUpdateListener) {
  connectionUpdateListeners.push(listener);
  return () => {
    const index = connectionUpdateListeners.indexOf(listener);
    if (index > -1) {
      connectionUpdateListeners.splice(index, 1);
    }
  };
}

function notifyConnectionUpdate() {
  connectionUpdateListeners.forEach(listener => listener());
}

/**
 * Get the current user's connections (matches, not pending)
 * FIXED: Now correctly fetches BOTH user profiles and returns the OTHER user
 */
export async function getConnections(): Promise<ConnectionsResult> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { success: true, connections: [] };

    // Fetch both user profiles so we can determine which one is the "other" user
    const { data, error } = await supabase
      .from('matches')
      .select(`
        id, user1_id, user2_id, status, initiated_by, created_at, updated_at,
        user1:profiles!matches_user1_id_fkey (id, username, display_name, avatar_url, meeple_avatar, bio, is_admin),
        user2:profiles!matches_user2_id_fkey (id, username, display_name, avatar_url, meeple_avatar, bio, is_admin)
      `)
      .eq('status', 'accepted')
      .or(`user1_id.eq.${user.id},user2_id.eq.${user.id}`);

    if (error) {
      logger.error('Error fetching connections:', error);
      return { success: false, connections: [], error: error.message };
    }

    // Map to Connection type, selecting the correct "other" user
    const connections: Connection[] = (data || [])
      .map(match => {
        // Determine which user is the "other" user (not the current user)
        const isUser1 = match.user1_id === user.id;
        const otherUserId = isUser1 ? match.user2_id : match.user1_id;
        const otherUserProfile = isUser1 ? match.user2 : match.user1;

        // Handle case where profile might be an array (shouldn't happen with proper foreign key)
        const otherUser = Array.isArray(otherUserProfile) ? otherUserProfile[0] : otherUserProfile;

        if (!otherUser) {
          logger.warn('Missing profile for match:', match.id);
          return null;
        }

        // Map meeple_avatar to avatar for application use
        (otherUser as any).avatar = otherUser.meeple_avatar;

        return {
          id: match.id,
          match_id: match.id,
          user1_id: match.user1_id,
          user2_id: match.user2_id,
          status: match.status,
          initiated_by: match.initiated_by,
          created_at: match.created_at,
          updated_at: match.updated_at,
          other_user: otherUser,
          otherUser: otherUser,
          otherUserId,
        } as Connection;
      })
      .filter((conn): conn is Connection => conn !== null);

    logger.log(`âœ… Loaded ${connections.length} connections for user ${user.id}`);
    return { success: true, connections };
  } catch (error) {
    logger.error('Error fetching connections:', error);
    return { success: false, connections: [], error: 'Failed to fetch connections' };
  }
}

/**
 * Get pending connection requests received by the current user
 */
export async function getPendingRequests(): Promise<ConnectionsResult> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { success: true, connections: [] };

    const { data, error } = await supabase
      .from('matches')
      .select(`
        id, user1_id, user2_id, status, initiated_by, created_at, updated_at,
        other_user:profiles!user1_id (id, username, display_name, avatar_url, meeple_avatar, bio, is_admin)
      `)
      .eq('status', 'pending')
      .eq('user2_id', user.id);

    if (error) {
      logger.error('Error fetching pending requests:', error);
      return { success: false, connections: [], error: error.message };
    }

    const pendingRequests: Connection[] = (data || []).map(match => {
      const profiles = Array.isArray(match.other_user) ? match.other_user : [match.other_user];
      const otherUser = profiles[0];

      // Map meeple_avatar to avatar for application use
      if (otherUser) {
        (otherUser as any).avatar = otherUser.meeple_avatar;
      }

      return {
        ...match,
        other_user: otherUser,
        otherUser: otherUser,
        otherUserId: match.user1_id,
        match_id: match.id,
      };
    });

    return { success: true, connections: pendingRequests };
  } catch (error) {
    logger.error('Error fetching pending requests:', error);
    return { success: false, connections: [], error: 'Failed to fetch pending requests' };
  }
}

/**
 * Get pending connection requests sent by the current user
 */
export async function getSentRequests(): Promise<MatchesResult> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { success: true, connections: [] };

    const { data, error } = await supabase
      .from('matches')
      .select(`
        id, user1_id, user2_id, status, initiated_by, created_at, updated_at,
        other_user:profiles!user2_id (id, username, display_name, avatar_url, meeple_avatar, bio, is_admin)
      `)
      .eq('status', 'pending')
      .eq('user1_id', user.id);

    if (error) {
      logger.error('Error fetching sent requests:', error);
      return { success: false, connections: [], error: error.message };
    }

    // Map to include other_user for consistency
    const sentRequests = (data || []).map(match => {
      const profiles = Array.isArray(match.other_user) ? match.other_user : [match.other_user];
      const otherUser = profiles[0];

      // Map meeple_avatar to avatar for application use
      if (otherUser) {
        (otherUser as any).avatar = otherUser.meeple_avatar;
      }

      return {
        ...match,
        other_user: otherUser,
      } as any;
    });

    return { success: true, connections: sentRequests };
  } catch (error) {
    logger.error('Error fetching sent requests:', error);
    return { success: false, connections: [], error: 'Failed to fetch sent requests' };
  }
}

/**
 * Send a connection request to a user.
 */
export async function sendConnectionRequest(
  targetUserId: string
): Promise<SendConnectionResult> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: 'Not authenticated' };
    }

    const { data: existingRequest, error: checkError } = await supabase
      .from('matches')
      .select('*')
      .eq('user1_id', targetUserId)
      .eq('user2_id', user.id)
      .eq('status', 'pending')
      .maybeSingle();

    if (checkError && checkError.code !== 'PGRST116') {
      logger.error('Error checking existing requests:', checkError);
      return { success: false, error: 'Failed to check existing request' };
    }

    if (existingRequest) {
      const { data: matchData, error: updateError } = await supabase
        .from('matches')
        .update({ status: 'accepted', updated_at: new Date().toISOString() })
        .eq('id', existingRequest.id)
        .select()
        .single();

      if (updateError) {
        logger.error('Error accepting match:', updateError);
        return { success: false, error: 'Failed to accept match' };
      }

      // Send push notification to the original requester
      await sendPushNotification({
        recipientId: targetUserId,
        senderId: user.id,
        type: 'connection_accepted',
        matchId: existingRequest.id,
      });

      notifyConnectionUpdate();
      return { success: true, match: matchData as Match };
    } else {
      const { data: newRequest, error: insertError } = await supabase
        .from('matches')
        .insert({
          user1_id: user.id,
          user2_id: targetUserId,
          status: 'pending',
          initiated_by: user.id,
        })
        .select()
        .single();

      if (insertError) {
        logger.error('Error sending request:', insertError);
        return { success: false, error: 'Failed to send connection request' };
      }

      // Send push notification to the target user
      await sendPushNotification({
        recipientId: targetUserId,
        senderId: user.id,
        type: 'new_connection_request',
        matchId: newRequest.id,
      });

      notifyConnectionUpdate();
      return { success: true };
    }
  } catch (error) {
    logger.error('Error in sendConnectionRequest:', error);
    return { success: false, error: 'An unexpected error occurred' };
  }
}

/**
 * Accept a pending connection request.
 */
export async function acceptConnectionRequest(
  matchId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: 'Not authenticated' };
    }

    // Get the match to find the requester
    const { data: match, error: fetchError } = await supabase
      .from('matches')
      .select('user1_id, user2_id')
      .eq('id', matchId)
      .single();

    if (fetchError) {
      logger.error('Error fetching match:', fetchError);
      return { success: false, error: 'Failed to fetch match' };
    }

    const { error } = await supabase
      .from('matches')
      .update({ status: 'accepted', updated_at: new Date().toISOString() })
      .eq('id', matchId);

    if (error) {
      logger.error('Error accepting request:', error);
      return { success: false, error: 'Failed to accept connection request' };
    }

    // Send push notification to the requester (user1)
    await sendPushNotification({
      recipientId: match.user1_id,
      senderId: user.id,
      type: 'connection_accepted',
      matchId: matchId,
    });

    notifyConnectionUpdate();
    return { success: true };
  } catch (error) {
    logger.error('Error in acceptConnectionRequest:', error);
    return { success: false, error: 'An unexpected error occurred' };
  }
}

/**
 * Reject a pending connection request.
 */
export async function rejectConnectionRequest(
  matchId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('matches')
      .update({ status: 'rejected', updated_at: new Date().toISOString() })
      .eq('id', matchId);

    if (error) {
      logger.error('Error rejecting request:', error);
      return { success: false, error: 'Failed to reject connection request' };
    }

    notifyConnectionUpdate();
    return { success: true };
  } catch (error) {
    logger.error('Error in rejectConnectionRequest:', error);
    return { success: false, error: 'An unexpected error occurred' };
  }
}

/**
 * Record a user rejection (swipe left) to prevent them from reappearing
 */
export async function recordRejection(
  targetUserId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: 'Not authenticated' };
    }

    // Check if there's already a match record between these users
    const { data: existingMatch } = await supabase
      .from('matches')
      .select('*')
      .or(`and(user1_id.eq.${user.id},user2_id.eq.${targetUserId}),and(user1_id.eq.${targetUserId},user2_id.eq.${user.id})`)
      .maybeSingle();

    if (existingMatch) {
      // If a match already exists, just update it to rejected
      const { error: updateError } = await supabase
        .from('matches')
        .update({ status: 'rejected', updated_at: new Date().toISOString() })
        .eq('id', existingMatch.id);

      if (updateError) {
        logger.error('Error updating rejection:', updateError);
        return { success: false, error: 'Failed to record rejection' };
      }
    } else {
      // Create a new rejected match record
      const { error: insertError } = await supabase
        .from('matches')
        .insert({
          user1_id: user.id,
          user2_id: targetUserId,
          status: 'rejected',
          initiated_by: user.id,
        });

      if (insertError) {
        logger.error('Error inserting rejection:', insertError);
        return { success: false, error: 'Failed to record rejection' };
      }
    }

    notifyConnectionUpdate();
    return { success: true };
  } catch (error) {
    logger.error('Error in recordRejection:', error);
    return { success: false, error: 'An unexpected error occurred' };
  }
}

/**
 * Cancel your own pending connection request (deletes the match).
 * This is different from rejecting someone else's request.
 */
export async function cancelConnectionRequest(
  matchId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: 'Not authenticated' };
    }

    // Verify this is your own request before deleting
    const { data: match } = await supabase
      .from('matches')
      .select('initiated_by')
      .eq('id', matchId)
      .single();

    if (!match) {
      return { success: false, error: 'Match not found' };
    }

    if (match.initiated_by !== user.id) {
      return { success: false, error: 'Can only cancel your own requests' };
    }

    // Delete the match entirely so they can re-appear in your queue
    const { error } = await supabase
      .from('matches')
      .delete()
      .eq('id', matchId);

    if (error) {
      logger.error('Error canceling request:', error);
      return { success: false, error: 'Failed to cancel connection request' };
    }

    notifyConnectionUpdate();
    return { success: true };
  } catch (error) {
    logger.error('Error in cancelConnectionRequest:', error);
    return { success: false, error: 'An unexpected error occurred' };
  }
}

/**
 * Block a user (will also delete connections/matches).
 */
export async function blockUser(
  targetUserId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: 'Not authenticated' };
    }

    const { error: blockError } = await supabase
      .from('blocked_users')
      .insert({
        blocker_id: user.id,
        blocked_id: targetUserId,
      });

    if (blockError) {
      logger.error('Error blocking user:', blockError);
      return { success: false, error: blockError.message };
    }

    const { error: deleteError } = await supabase
      .from('matches')
      .delete()
      .or(`and(user1_id.eq.${user.id},user2_id.eq.${targetUserId}),and(user1_id.eq.${targetUserId},user2_id.eq.${user.id})`);

    if (deleteError) {
      logger.error('Error deleting matches:', deleteError);
    }

    notifyConnectionUpdate();
    return { success: true };
  } catch (error) {
    logger.error('Error in blockUser:', error);
    return { success: false, error: 'Failed to block user' };
  }
}

/**
 * Unblock a user
 */
export async function unblockUser(
  targetUserId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: 'Not authenticated' };
    }

    const { error } = await supabase
      .from('blocked_users')
      .delete()
      .eq('blocker_id', user.id)
      .eq('blocked_id', targetUserId);

    if (error) {
      logger.error('Error unblocking user:', error);
      return { success: false, error: 'Failed to unblock user' };
    }

    notifyConnectionUpdate();
    return { success: true };
  } catch (error) {
    logger.error('Error in unblockUser:', error);
    return { success: false, error: 'Failed to unblock user' };
  }
}

/**
 * Check if the current user has blocked a target user
 */
export async function isBlocked(
  targetUserId: string
): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const { count, error } = await supabase
      .from('blocked_users')
      .select('*', { count: 'exact' })
      .eq('blocker_id', user.id)
      .eq('blocked_id', targetUserId);

    if (error) {
      logger.error('Error checking blocked status:', error);
      return false;
    }

    return (count || 0) > 0;
  } catch (error) {
    logger.error('Error in isBlocked:', error);
    return false;
  }
}

/**
 * Check if the current user is blocked by a target user
 */
export async function amIBlocked(
  targetUserId: string
): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const { count, error } = await supabase
      .from('blocked_users')
      .select('*', { count: 'exact' })
      .eq('blocker_id', targetUserId)
      .eq('blocked_id', user.id);

    if (error) {
      logger.error('Error checking blocked status:', error);
      return false;
    }

    return (count || 0) > 0;
  } catch (error) {
    logger.error('Error in amIBlocked:', error);
    return false;
  }
}

/**
 * Get connection status with a specific user
 * Returns 'none', 'pending', or 'connected'
 */
export async function getConnectionStatus(
  targetUserId: string
): Promise<'none' | 'pending' | 'connected'> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return 'none';

    // Check for any match between current user and target user
    const { data, error } = await supabase
      .from('matches')
      .select('status')
      .or(`and(user1_id.eq.${user.id},user2_id.eq.${targetUserId}),and(user1_id.eq.${targetUserId},user2_id.eq.${user.id})`)
      .maybeSingle();

    if (error && error.code !== 'PGRST116') {
      logger.error('Error checking connection status:', error);
      return 'none';
    }

    if (!data) return 'none';
    if (data.status === 'accepted') return 'connected';
    if (data.status === 'pending') return 'pending';

    return 'none';
  } catch (error) {
    logger.error('Error in getConnectionStatus:', error);
    return 'none';
  }
}